import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { push } from "svelte-spa-router";
import * as Common from "../utiles/common.util";
import * as store from "../stores/store";
import { Preferences } from "@capacitor/preferences";

const version = "/api/v1/";
const isNative = Capacitor.isNativePlatform();
const isBrowser = !isNative;
const isEmulator =
    (isNative && /10\.0\.2\.2/.test(window.location.href)) || false;

// 기본 baseURL
let baseURL = "";

// 1. 모바일 앱 (실제 디바이스)
if (isNative && !isEmulator) {
    // 실제 모바일에서 로컬 서버 접속
    // baseURL = "http://192.168.0.42" + version;
    baseURL = "https://praimas.asijang.co.kr" + version;
}

// 2. 모바일 에뮬레이터
else if (isNative && isEmulator) {
    baseURL = "http://10.0.2.2" + version; // 에뮬레이터 localhost
}

// 3. 웹 브라우저
else if (isBrowser) {
    const host = window.location.hostname;
    const isLocalHost = host !== "praimas.asijang.co.kr" && host !== "aspraim.asijang.co.kr";
    baseURL = isLocalHost
        ? "http://" + host + version // 개발용 (어떤 IP/도메인 대역이든 브라우저의 host 사용)
        : "https://praimas.asijang.co.kr" + version; // 운영용
}

const instance = axios.create({
    baseURL,
    timeout: 54000,
    withCredentials: true,
});

let isRefreshing = false;
let subscribers = [];

function onRefreshed(newToken) {
    subscribers.forEach((callback) => callback(newToken));
    subscribers = [];
}

// 요청/응답 인터셉터 추가
instance.interceptors.request.use(
    async (config) => {
        const token = await Common.getStoredToken();

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error),
);

instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config, response } = error;
        const originalRequest = config;

        // 401 에러이고, 재시도한 적이 없을 때만 실행
        if (response && response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // 이미 토큰 갱신 중이라면 대기열(subscribers)에 추가
            if (isRefreshing) {
                return new Promise((resolve) => {
                    subscribers.push((newToken) => {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        resolve(instance(originalRequest));
                    });
                });
            }

            isRefreshing = true;

            try {
                const { value: praimas_jwt_token } = await Preferences.get({
                    key: "praimas_jwt_token",
                });

                if (!praimas_jwt_token) {
                    throw new Error("No refresh token available");
                }

                // 토큰 갱신 요청 (반드시 기본 axios 사용)
                const formData = {
                    JWT_TOKEN: praimas_jwt_token,
                };

                const refreshRes = await axios.post(
                    baseURL + "account/refresh",
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );

                const newAccessToken = refreshRes.data.token.accessToken;

                // 새로운 토큰들 저장 (Preferences + Store)
                await Preferences.set({
                    key: "praimas_jwt_token",
                    value: newAccessToken,
                });
                store.jwt_token.set(newAccessToken);

                isRefreshing = false;
                onRefreshed(newAccessToken);

                // // 원래 실패했던 요청 재시도
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return instance(originalRequest);
            } catch (refreshError) {
                // 리프레시 토큰마저 만료되었거나 에러 발생 시 로그아웃
                isRefreshing = false;
                // [디버그 로그] 로직과 무관, 에러 확인용 콘솔 출력 - 필요시 이 라인만 찾아 수정/제거
                console.error("Token refresh failed:", refreshError);

                localStorage.clear();
                await Preferences.clear();
                store.accountInfo.set(null);
                store.jwt_token.set(null);

                push("/Login");
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    },
);

export default instance;

// [비활성 코드] 로직과 무관, 전체 주석 처리된 미사용 401 인터셉터 - 필요시 이 블록만 찾아 수정/제거
// instance.interceptors.response.use(
//     (response) => response,
//     async (error) => {
//         // 서버에서 401을 던지면 error.response가 존재하게 됨
//         if (error.response && error.response.status === 401) {
//             console.log("토큰 만료 감지 -> 로그아웃 실행");

//             // 1. 모든 저장소 및 스토어 비우기
//             localStorage.clear();
//             await Preferences.clear();
//             store.accountInfo.set(null);
//             store.jwt_token.set(null);

//             // 2. 로그인 페이지로 튕기기
//             push("/Login");

//             return Promise.reject(error);
//         }

//         // 401이 아닌 다른 에러는 그냥 에러 처리
//         return Promise.reject(error);
//     },
// );

// export default instance;
